// ============================================================================
// SAMPLE SERVICE ENDPOINTS (Reference Implementation)
// ============================================================================
// Endpoint definitions for Service Master module

import { buildEndpoint, buildEndpointWithParams } from './base.endpoints';
import type { ID } from '../types';

/**
 * Base path for service endpoints
 */
const BASE_PATH = '/services';

/**
 * Service Endpoints
 */
export const sampleServiceEndpoints = {
  /**
   * Get all services (paginated)
   * GET /services
   */
  list: () => buildEndpoint(BASE_PATH),

  /**
   * Get service by ID
   * GET /services/:id
   */
  getById: (id: ID) => buildEndpointWithParams(`${BASE_PATH}/:id`, { id }),

  /**
   * Create new service
   * POST /services
   */
  create: () => buildEndpoint(BASE_PATH),

  /**
   * Update service
   * PUT /services/:id
   */
  update: (id: ID) => buildEndpointWithParams(`${BASE_PATH}/:id`, { id }),

  /**
   * Delete service
   * DELETE /services/:id
   */
  delete: (id: ID) => buildEndpointWithParams(`${BASE_PATH}/:id`, { id }),

  /**
   * Search services
   * POST /services/search
   */
  search: () => buildEndpoint(`${BASE_PATH}/search`),

  /**
   * Get similar service names
   * GET /services/similar/:name
   */
  similar: (name: string) =>
    buildEndpointWithParams(`${BASE_PATH}/similar/:name`, { name }),

  /**
   * Validate service data
   * POST /services/validate
   */
  validate: () => buildEndpoint(`${BASE_PATH}/validate`),

  /**
   * Bulk create services
   * POST /services/bulk
   */
  bulkCreate: () => buildEndpoint(`${BASE_PATH}/bulk`),

  /**
   * Bulk update services
   * PUT /services/bulk
   */
  bulkUpdate: () => buildEndpoint(`${BASE_PATH}/bulk`),

  /**
   * Bulk delete services
   * DELETE /services/bulk
   */
  bulkDelete: () => buildEndpoint(`${BASE_PATH}/bulk`),

  /**
   * Export services
   * GET /services/export
   */
  export: () => buildEndpoint(`${BASE_PATH}/export`),

  /**
   * Import services
   * POST /services/import
   */
  import: () => buildEndpoint(`${BASE_PATH}/import`),

  /**
   * Get services by category
   * GET /services/by-category/:categoryId
   */
  byCategory: (categoryId: ID) =>
    buildEndpointWithParams(`${BASE_PATH}/by-category/:categoryId`, { categoryId }),

  /**
   * Get services by entity
   * GET /services/by-entity/:entityId
   */
  byEntity: (entityId: ID) =>
    buildEndpointWithParams(`${BASE_PATH}/by-entity/:entityId`, { entityId }),

  /**
   * Get active services
   * GET /services/active
   */
  active: () => buildEndpoint(`${BASE_PATH}/active`),

  /**
   * Get service statistics
   * GET /services/statistics
   */
  statistics: () => buildEndpoint(`${BASE_PATH}/statistics`),
} as const;

/**
 * Export endpoints
 */
export default sampleServiceEndpoints;
